import React, { useState } from 'react'

import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Chip,
    Grid,
    Divider,
    useTheme,
    Card,
    CardContent,
    Tabs,
    Tab,
} from '@material-ui/core'
import { Flag as CampaignIcon } from '@material-ui/icons'
import { makeStyles, Theme } from '@material-ui/core/styles'

import {
    Prospect,
    DetailedCampaignProspect,
    Lead,
    LeadSource,
    LeadSyncStatus,
    LeadSyncStatusDisplay,
} from '../../../../models/Outreach/types'
import { getPipelineStage, getStatusColor } from '../utils'
import { CampaignEmailUnsubscribeIndicator } from '../../CampaignEmailUnsubscribeIndicator'
import { GUEST_CARD_FIELD_QUESTIONS } from '../../../../models/Outreach/services'
import { LeadApplicationPanel } from './LeadApplicationPanel'
import { LeadScreeningPanel } from './LeadScreeningPanel'

interface LeadDetailDialogProps {
    open: boolean
    onClose: () => void
    lead: Lead | null
    prospect?: Prospect | null
    campaignProspect?: DetailedCampaignProspect | null
    apartmentId?: number
    initialTab?: number
    onSyncLead?: (leadId: number) => void
    onInviteToApply?: (leadId: number) => Promise<unknown>
    onApproveApplication?: (leadId: number) => Promise<unknown>
    onSendScreeningLink?: (leadId: number) => Promise<unknown>
    onStartScreening?: (leadId: number) => Promise<unknown>
    onResendScreeningInvitation?: (leadId: number) => Promise<unknown>
    isApprovingApplication?: boolean
    isSendingScreeningLink?: boolean
    isStartingScreening?: boolean
    isResendingScreeningInvitation?: boolean
}

const useStyles = makeStyles((theme: Theme) => ({
    dialog: {
        '& .MuiDialog-paper': {
            maxWidth: '900px',
            width: '100%',
            maxHeight: 'calc(100vh - 64px)',
            height: 'calc(100vh - 64px)',
            display: 'flex',
            flexDirection: 'column',
            margin: theme.spacing(4),
        },
    },
    dialogTitle: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: theme.spacing(2.5, 3),
        borderBottom: `1px solid ${theme.palette.divider}`,
        backgroundColor: '#f8fafc',
        flexShrink: 0,
    },
    dialogContent: {
        padding: 0,
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        minHeight: 0,
    },
    contentWrapper: {
        padding: theme.spacing(3),
        flex: 1,
        overflowY: 'auto',
        minHeight: 0,
    },
    section: {
        marginBottom: theme.spacing(3),
    },
    sectionTitle: {
        fontSize: '1.125rem',
        fontWeight: 600,
        marginBottom: theme.spacing(2),
        color: theme.palette.text.primary,
        paddingBottom: theme.spacing(1),
        borderBottom: `2px solid ${theme.palette.primary.main}`,
    },
    fieldRow: {
        display: 'flex',
        alignItems: 'flex-start',
        marginBottom: theme.spacing(2),
        gap: theme.spacing(2),
        padding: theme.spacing(1, 0),
    },
    fieldLabel: {
        minWidth: '160px',
        fontSize: '0.875rem',
        color: theme.palette.text.secondary,
        fontWeight: 600,
    },
    fieldValue: {
        flex: 1,
        fontSize: '0.875rem',
        color: theme.palette.text.primary,
        wordBreak: 'break-word',
    },
    chip: {
        marginRight: theme.spacing(1),
        marginBottom: theme.spacing(0.5),
    },
    engagementCard: {
        marginBottom: theme.spacing(2),
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        },
    },
    engagementMetric: {
        textAlign: 'center',
        padding: theme.spacing(2),
    },
    engagementValue: {
        fontSize: '2rem',
        fontWeight: 'bold',
        color: theme.palette.primary.main,
        marginBottom: theme.spacing(0.5),
    },
    engagementLabel: {
        fontSize: '0.875rem',
        color: theme.palette.text.secondary,
        fontWeight: 500,
    },
    tabs: {
        borderBottom: `2px solid ${theme.palette.divider}`,
        backgroundColor: '#ffffff',
        flexShrink: 0,
        '& .MuiTab-root': {
            textTransform: 'none',
            fontSize: '0.9375rem',
            fontWeight: 500,
            minHeight: 48,
            padding: theme.spacing(1.5, 3),
        },
        '& .Mui-selected': {
            color: theme.palette.primary.main,
            fontWeight: 600,
        },
    },
    tabPanel: {
        paddingTop: theme.spacing(2),
        flex: 1,
        overflowY: 'auto',
        minHeight: 0,
    },
    divider: {
        margin: theme.spacing(2, 0),
        backgroundColor: theme.palette.divider,
    },
    roommateCard: {
        padding: theme.spacing(2),
        backgroundColor: '#f9fafb',
        borderRadius: '8px',
        border: '1px solid #e5e7eb',
        marginBottom: theme.spacing(1.5),
        transition: 'all 0.2s',
        '&:hover': {
            backgroundColor: '#f3f4f6',
            borderColor: theme.palette.primary.light,
        },
    },
    incompleteNotice: {
        padding: theme.spacing(1.5, 2),
        marginBottom: theme.spacing(2),
        backgroundColor: '#fffbeb',
        borderRadius: '8px',
        border: '1px solid #fde68a',
    },
    unansweredValue: {
        color: theme.palette.text.secondary,
        fontStyle: 'italic',
    },
}))

const formatCurrency = (amount: number | null): string => {
    if (amount === null) return 'N/A'
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(amount)
}

const formatDate = (dateString: string | null): string => {
    if (!dateString) return 'N/A'
    try {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        })
    } catch {
        return 'N/A'
    }
}

export const LeadDetailDialog: React.FC<LeadDetailDialogProps> = ({
    open,
    onClose,
    lead,
    prospect,
    campaignProspect,
    apartmentId,
    initialTab = 0,
    onSyncLead,
    onInviteToApply,
    onApproveApplication,
    onSendScreeningLink,
    onStartScreening,
    onResendScreeningInvitation,
    isApprovingApplication = false,
    isSendingScreeningLink = false,
    isStartingScreening = false,
    isResendingScreeningInvitation = false,
}) => {
    const classes = useStyles()
    const theme = useTheme()
    const [activeTab, setActiveTab] = useState(initialTab)

    React.useEffect(() => {
        if (open) {
            setActiveTab(initialTab)
        }
    }, [open, initialTab, lead?.id])

    if (!lead) return null

    const prospectData = prospect || lead.campaign_prospect?.prospect
    const campaignProspectData = lead.campaign_prospect || campaignProspect
    const pipelineStage = campaignProspectData
        ? getPipelineStage(campaignProspectData)
        : 'lead'
    const statusColor = getStatusColor(pipelineStage)

    const handleTabChange = (_: React.ChangeEvent<{}>, newValue: number) => {
        setActiveTab(newValue)
    }

    const renderGuestCardAnswer = (value: string | null | undefined) => (
        <Typography
            className={`${classes.fieldValue} ${
                value ? '' : classes.unansweredValue
            }`}
        >
            {value || 'Not answered'}
        </Typography>
    )

    return (
        <Dialog
            open={open}
            onClose={onClose}
            className={classes.dialog}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle className={classes.dialogTitle}>
                <Box
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: theme.spacing(2),
                    }}
                >
                    <Typography variant="h6">Lead Details</Typography>
                </Box>
            </DialogTitle>

            <DialogContent className={classes.dialogContent}>
                <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    className={classes.tabs}
                    indicatorColor="primary"
                    textColor="primary"
                >
                    <Tab label="Guest Card" />
                    <Tab label="Application" />
                    <Tab label="Screening" />
                    <Tab label="Campaign & Engagement" />
                </Tabs>

                <Box className={classes.contentWrapper}>
                    {/* Tab 1: Guest Card */}
                    {activeTab === 0 && (
                        <Box className={classes.tabPanel}>
                            {/* Basic Info */}
                            <Box className={classes.section}>
                                <Typography className={classes.sectionTitle}>
                                    Basic Information
                                </Typography>
                                <Box className={classes.fieldRow}>
                                    <Typography className={classes.fieldLabel}>
                                        Name
                                    </Typography>
                                    <Box
                                        style={{
                                            flex: 1,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'flex-start',
                                            gap: 8,
                                        }}
                                    >
                                        <Typography
                                            className={classes.fieldValue}
                                        >
                                            {prospectData?.name || 'N/A'}
                                        </Typography>
                                        <CampaignEmailUnsubscribeIndicator
                                            prospect={prospectData}
                                        />
                                    </Box>
                                </Box>
                                <Box className={classes.fieldRow}>
                                    <Typography className={classes.fieldLabel}>
                                        Guest Card
                                    </Typography>
                                    <Typography className={classes.fieldValue}>
                                        {lead.guest_card?.card_name || 'N/A'}
                                    </Typography>
                                </Box>
                                <Box className={classes.fieldRow}>
                                    <Typography className={classes.fieldLabel}>
                                        Guest Card Completed
                                    </Typography>
                                    <Box style={{ flex: 1 }}>
                                        <Chip
                                            label={
                                                lead.guest_card_completed
                                                    ? 'Yes'
                                                    : 'No'
                                            }
                                            size="small"
                                            style={{
                                                backgroundColor: lead.guest_card_completed
                                                    ? '#10b981'
                                                    : '#6b7280',
                                                color: 'white',
                                            }}
                                        />
                                    </Box>
                                </Box>
                                <Box className={classes.fieldRow}>
                                    <Typography className={classes.fieldLabel}>
                                        Sync Status
                                    </Typography>
                                    <Box style={{ flex: 1 }}>
                                        <Chip
                                            label={
                                                lead.sync_status ===
                                                LeadSyncStatus.SYNCED
                                                    ? LeadSyncStatusDisplay.SYNCED
                                                    : lead.sync_status ===
                                                      LeadSyncStatus.SYNC_PENDING
                                                    ? LeadSyncStatusDisplay.SYNC_PENDING
                                                    : lead.sync_status ===
                                                      LeadSyncStatus.SYNC_FAILED
                                                    ? LeadSyncStatusDisplay.SYNC_FAILED
                                                    : LeadSyncStatusDisplay.NOT_SYNCED
                                            }
                                            size="small"
                                            style={{
                                                backgroundColor:
                                                    lead.sync_status ===
                                                    LeadSyncStatus.SYNCED
                                                        ? '#10b981'
                                                        : lead.sync_status ===
                                                          LeadSyncStatus.SYNC_PENDING
                                                        ? '#f59e0b'
                                                        : lead.sync_status ===
                                                          LeadSyncStatus.SYNC_FAILED
                                                        ? '#ef4444'
                                                        : '#6b7280',
                                                color: 'white',
                                            }}
                                        />
                                    </Box>
                                </Box>
                                {lead.entrata_id && (
                                    <Box className={classes.fieldRow}>
                                        <Typography
                                            className={classes.fieldLabel}
                                        >
                                            Entrata ID
                                        </Typography>
                                        <Typography
                                            className={classes.fieldValue}
                                        >
                                            {lead.entrata_id}
                                        </Typography>
                                    </Box>
                                )}
                                {lead.last_sync_attempted_at && (
                                    <Box className={classes.fieldRow}>
                                        <Typography
                                            className={classes.fieldLabel}
                                        >
                                            Last Sync Attempt
                                        </Typography>
                                        <Typography
                                            className={classes.fieldValue}
                                        >
                                            {formatDate(
                                                lead.last_sync_attempted_at,
                                            )}
                                        </Typography>
                                    </Box>
                                )}
                                <Box className={classes.fieldRow}>
                                    <Typography className={classes.fieldLabel}>
                                        Lead Source
                                    </Typography>
                                    <Box style={{ flex: 1 }}>
                                        <Chip
                                            label={
                                                lead.lead_source
                                                    .charAt(0)
                                                    .toUpperCase() +
                                                lead.lead_source.slice(1)
                                            }
                                            size="small"
                                            style={{
                                                backgroundColor:
                                                    lead.lead_source ===
                                                    LeadSource.EMAIL
                                                        ? '#10b981'
                                                        : lead.lead_source ===
                                                          LeadSource.PHONE
                                                        ? '#f59e0b'
                                                        : '#8b5cf6',
                                                color: 'white',
                                            }}
                                        />
                                    </Box>
                                </Box>
                            </Box>

                            <Divider className={classes.divider} />

                            {/* Guest Card Information */}
                            <Box className={classes.section}>
                                <Box
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: theme.spacing(1.5),
                                        marginBottom: theme.spacing(2),
                                        paddingBottom: theme.spacing(1),
                                        borderBottom: `2px solid ${theme.palette.primary.main}`,
                                    }}
                                >
                                    <Typography
                                        className={classes.sectionTitle}
                                        style={{
                                            marginBottom: 0,
                                            paddingBottom: 0,
                                            borderBottom: 'none',
                                            flex: 1,
                                        }}
                                    >
                                        Guest Card Information
                                    </Typography>
                                    {!lead.guest_card_completed && (
                                        <Chip
                                            label="Incomplete"
                                            size="small"
                                            style={{
                                                backgroundColor: '#f59e0b',
                                                color: 'white',
                                            }}
                                        />
                                    )}
                                </Box>
                                {!lead.guest_card_completed && (
                                    <Box className={classes.incompleteNotice}>
                                        <Typography
                                            variant="body2"
                                            style={{ color: '#92400e' }}
                                        >
                                            This guest card is not complete yet.
                                            Any answers collected so far are
                                            shown below.
                                        </Typography>
                                    </Box>
                                )}

                                {/* Contact Information Fields */}
                                {(lead.guest_card?.name ||
                                    prospectData?.name) && (
                                    <Box className={classes.fieldRow}>
                                        <Typography
                                            className={classes.fieldLabel}
                                        >
                                            {GUEST_CARD_FIELD_QUESTIONS.name}
                                        </Typography>
                                        {renderGuestCardAnswer(
                                            prospectData?.name,
                                        )}
                                    </Box>
                                )}
                                {(lead.guest_card?.email ||
                                    prospectData?.email?.value) && (
                                    <Box className={classes.fieldRow}>
                                        <Typography
                                            className={classes.fieldLabel}
                                        >
                                            {GUEST_CARD_FIELD_QUESTIONS.email}
                                        </Typography>
                                        {renderGuestCardAnswer(
                                            prospectData?.email?.value,
                                        )}
                                    </Box>
                                )}
                                {(lead.guest_card?.phone ||
                                    prospectData?.phone?.value) && (
                                    <Box className={classes.fieldRow}>
                                        <Typography
                                            className={classes.fieldLabel}
                                        >
                                            {GUEST_CARD_FIELD_QUESTIONS.phone}
                                        </Typography>
                                        {renderGuestCardAnswer(
                                            prospectData?.phone?.value,
                                        )}
                                    </Box>
                                )}

                                {/* Additional Fields (excluding roommates) */}
                                {(lead.guest_card?.preferred_unit_config ||
                                    lead.preferred_unit_config) && (
                                    <Box className={classes.fieldRow}>
                                        <Typography
                                            className={classes.fieldLabel}
                                        >
                                            {
                                                GUEST_CARD_FIELD_QUESTIONS.preferred_unit_config
                                            }
                                        </Typography>
                                        {renderGuestCardAnswer(
                                            lead.preferred_unit_config?.name,
                                        )}
                                    </Box>
                                )}
                                {lead.guest_card.preferred_premiums && (
                                    <Box className={classes.fieldRow}>
                                        <Typography
                                            className={classes.fieldLabel}
                                        >
                                            {
                                                GUEST_CARD_FIELD_QUESTIONS.preferred_premiums
                                            }
                                        </Typography>
                                        <Typography
                                            className={classes.fieldValue}
                                        >
                                            {lead.preferred_premiums &&
                                            lead.preferred_premiums.length > 0
                                                ? lead.preferred_premiums
                                                      .map(
                                                          (premium) =>
                                                              premium.name,
                                                      )
                                                      .join(', ')
                                                : 'No'}
                                        </Typography>
                                    </Box>
                                )}
                                {(lead.guest_card?.preferred_rent_cost ||
                                    lead.preferred_rent_cost != null) && (
                                    <Box className={classes.fieldRow}>
                                        <Typography
                                            className={classes.fieldLabel}
                                        >
                                            {
                                                GUEST_CARD_FIELD_QUESTIONS.preferred_rent_cost
                                            }
                                        </Typography>
                                        {renderGuestCardAnswer(
                                            lead.preferred_rent_cost != null
                                                ? formatCurrency(
                                                      lead.preferred_rent_cost,
                                                  )
                                                : null,
                                        )}
                                    </Box>
                                )}
                                {(lead.guest_card?.preferred_move_in_date ||
                                    lead.preferred_move_in_date) && (
                                    <Box className={classes.fieldRow}>
                                        <Typography
                                            className={classes.fieldLabel}
                                        >
                                            {
                                                GUEST_CARD_FIELD_QUESTIONS.preferred_move_in_date
                                            }
                                        </Typography>
                                        {renderGuestCardAnswer(
                                            lead.preferred_move_in_date
                                                ? formatDate(
                                                      lead.preferred_move_in_date,
                                                  )
                                                : null,
                                        )}
                                    </Box>
                                )}
                                {lead.guest_card.preferred_move_out_date && (
                                    <Box className={classes.fieldRow}>
                                        <Typography
                                            className={classes.fieldLabel}
                                        >
                                            {
                                                GUEST_CARD_FIELD_QUESTIONS.preferred_move_out_date
                                            }
                                        </Typography>
                                        <Typography
                                            className={classes.fieldValue}
                                        >
                                            {lead.preferred_move_out_date
                                                ? formatDate(
                                                      lead.preferred_move_out_date,
                                                  )
                                                : 'N/A'}
                                        </Typography>
                                    </Box>
                                )}
                                {(lead.guest_card?.school_attendance ||
                                    lead.school_attendance_name) && (
                                    <Box className={classes.fieldRow}>
                                        <Typography
                                            className={classes.fieldLabel}
                                        >
                                            {
                                                GUEST_CARD_FIELD_QUESTIONS.school_attendance
                                            }
                                        </Typography>
                                        {renderGuestCardAnswer(
                                            lead.school_attendance_name,
                                        )}
                                    </Box>
                                )}
                                {(lead.guest_card?.additional_requests ||
                                    lead.additional_requests) && (
                                    <Box className={classes.fieldRow}>
                                        <Typography
                                            className={classes.fieldLabel}
                                        >
                                            {
                                                GUEST_CARD_FIELD_QUESTIONS.additional_requests
                                            }
                                        </Typography>
                                        {renderGuestCardAnswer(
                                            lead.additional_requests,
                                        )}
                                    </Box>
                                )}

                                {/* Custom Questions */}
                                {lead.guest_card?.card_questions &&
                                    lead.guest_card.card_questions.length >
                                        0 && (
                                        <>
                                            {lead.guest_card.card_questions.map(
                                                (cardQuestion) => {
                                                    const answer = lead.custom_answers?.find(
                                                        (ans) =>
                                                            ans.guest_card_custom_question ===
                                                            cardQuestion
                                                                .custom_question
                                                                .id,
                                                    )
                                                    return (
                                                        <Box
                                                            key={
                                                                cardQuestion
                                                                    .custom_question
                                                                    .id
                                                            }
                                                            className={
                                                                classes.fieldRow
                                                            }
                                                        >
                                                            <Typography
                                                                className={
                                                                    classes.fieldLabel
                                                                }
                                                            >
                                                                {
                                                                    cardQuestion
                                                                        .custom_question
                                                                        .question
                                                                }
                                                            </Typography>
                                                            {renderGuestCardAnswer(
                                                                answer?.answer,
                                                            )}
                                                        </Box>
                                                    )
                                                },
                                            )}
                                        </>
                                    )}

                                {/* Preferred Roommates - Always Last */}
                                {(lead.guest_card?.preferred_roommates ||
                                    (lead.preferred_roommates &&
                                        lead.preferred_roommates.length >
                                            0)) && (
                                    <Box
                                        style={{
                                            marginTop: theme.spacing(2),
                                        }}
                                    >
                                        <Typography
                                            className={classes.fieldLabel}
                                            style={{
                                                marginBottom: theme.spacing(
                                                    1.5,
                                                ),
                                            }}
                                        >
                                            {
                                                GUEST_CARD_FIELD_QUESTIONS.preferred_roommates
                                            }
                                        </Typography>
                                        {lead.preferred_roommates &&
                                        lead.preferred_roommates.length > 0 ? (
                                            <Box>
                                                {lead.preferred_roommates.map(
                                                    (roommate, index) => (
                                                        <Box
                                                            key={roommate.id}
                                                            className={
                                                                classes.roommateCard
                                                            }
                                                        >
                                                            <Typography
                                                                variant="body2"
                                                                style={{
                                                                    fontWeight: 600,
                                                                    marginBottom: theme.spacing(
                                                                        0.5,
                                                                    ),
                                                                }}
                                                            >
                                                                Roommate{' '}
                                                                {index + 1}
                                                            </Typography>
                                                            <Typography variant="body2">
                                                                <strong>
                                                                    Name:
                                                                </strong>{' '}
                                                                {roommate.name ||
                                                                    'Not answered'}
                                                            </Typography>
                                                            <Typography variant="body2">
                                                                <strong>
                                                                    Email:
                                                                </strong>{' '}
                                                                {roommate.email ||
                                                                    'Not answered'}
                                                            </Typography>
                                                            <Typography variant="body2">
                                                                <strong>
                                                                    Phone:
                                                                </strong>{' '}
                                                                {roommate.phone ||
                                                                    'Not answered'}
                                                            </Typography>
                                                        </Box>
                                                    ),
                                                )}
                                            </Box>
                                        ) : (
                                            <Typography
                                                variant="body2"
                                                className={
                                                    classes.unansweredValue
                                                }
                                            >
                                                Not answered
                                            </Typography>
                                        )}
                                    </Box>
                                )}
                            </Box>

                            {/* Custom Data */}
                            {prospectData?.custom_data &&
                                Object.keys(prospectData.custom_data).length >
                                    0 && (
                                    <>
                                        <Divider className={classes.divider} />
                                        <Box className={classes.section}>
                                            <Typography
                                                className={classes.sectionTitle}
                                            >
                                                Custom Data
                                            </Typography>
                                            {Object.entries(
                                                prospectData.custom_data,
                                            ).map(([key, value]) => (
                                                <Box
                                                    key={key}
                                                    className={classes.fieldRow}
                                                >
                                                    <Typography
                                                        className={
                                                            classes.fieldLabel
                                                        }
                                                    >
                                                        {key
                                                            .replace(/_/g, ' ')
                                                            .replace(
                                                                /\b\w/g,
                                                                (l) =>
                                                                    l.toUpperCase(),
                                                            )}
                                                    </Typography>
                                                    <Typography
                                                        className={
                                                            classes.fieldValue
                                                        }
                                                    >
                                                        {value
                                                            ? String(value)
                                                            : '—'}
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Box>
                                    </>
                                )}
                        </Box>
                    )}

                    {/* Tab 2: Application */}
                    {activeTab === 1 && (
                        <Box className={classes.tabPanel}>
                            <LeadApplicationPanel
                                lead={lead}
                                onInviteToApply={onInviteToApply}
                                onApproveApplication={onApproveApplication}
                                onSendScreeningLink={onSendScreeningLink}
                                isApproving={isApprovingApplication}
                                isSendingScreeningLink={isSendingScreeningLink}
                            />
                        </Box>
                    )}

                    {/* Tab 3: Screening */}
                    {activeTab === 2 && (
                        <Box className={classes.tabPanel}>
                            <LeadScreeningPanel
                                lead={lead}
                                onStartScreening={onStartScreening}
                                onResendScreeningInvitation={
                                    onResendScreeningInvitation
                                }
                                isStarting={isStartingScreening}
                                isResending={isResendingScreeningInvitation}
                            />
                        </Box>
                    )}

                    {/* Tab 4: Campaign & Engagement */}
                    {activeTab === 3 && (
                        <Box className={classes.tabPanel}>
                            {/* Campaign Information */}
                            {campaignProspectData ? (
                                <>
                                    <Box className={classes.section}>
                                        <Typography
                                            className={classes.sectionTitle}
                                        >
                                            Campaign Information
                                        </Typography>
                                        <Box className={classes.fieldRow}>
                                            <Typography
                                                className={classes.fieldLabel}
                                            >
                                                Campaign
                                            </Typography>
                                            <Box style={{ flex: 1 }}>
                                                <Chip
                                                    icon={
                                                        <CampaignIcon
                                                            style={{
                                                                fontSize: 16,
                                                            }}
                                                        />
                                                    }
                                                    label={
                                                        campaignProspectData
                                                            .campaign.name
                                                    }
                                                    className={classes.chip}
                                                    style={{
                                                        backgroundColor:
                                                            '#10b981',
                                                        color: 'white',
                                                    }}
                                                />
                                            </Box>
                                        </Box>
                                        <Box className={classes.fieldRow}>
                                            <Typography
                                                className={classes.fieldLabel}
                                            >
                                                Status
                                            </Typography>
                                            <Box style={{ flex: 1 }}>
                                                <Chip
                                                    label={
                                                        campaignProspectData.status_display ||
                                                        campaignProspectData.status
                                                    }
                                                    size="small"
                                                    style={{
                                                        backgroundColor: statusColor,
                                                        color: 'white',
                                                    }}
                                                />
                                            </Box>
                                        </Box>
                                        {campaignProspectData.current_step && (
                                            <Box className={classes.fieldRow}>
                                                <Typography
                                                    className={
                                                        classes.fieldLabel
                                                    }
                                                >
                                                    Current Step
                                                </Typography>
                                                <Typography
                                                    className={
                                                        classes.fieldValue
                                                    }
                                                >
                                                    Step{' '}
                                                    {
                                                        campaignProspectData
                                                            .current_step
                                                            .step_order
                                                    }
                                                    :{' '}
                                                    {
                                                        campaignProspectData
                                                            .current_step
                                                            .step_type
                                                    }
                                                </Typography>
                                            </Box>
                                        )}
                                        {campaignProspectData.last_step_sent_at && (
                                            <Box className={classes.fieldRow}>
                                                <Typography
                                                    className={
                                                        classes.fieldLabel
                                                    }
                                                >
                                                    Last Step Sent
                                                </Typography>
                                                <Typography
                                                    className={
                                                        classes.fieldValue
                                                    }
                                                >
                                                    {new Date(
                                                        campaignProspectData.last_step_sent_at,
                                                    ).toLocaleString()}
                                                </Typography>
                                            </Box>
                                        )}
                                        {campaignProspectData.next_step_scheduled_at && (
                                            <Box className={classes.fieldRow}>
                                                <Typography
                                                    className={
                                                        classes.fieldLabel
                                                    }
                                                >
                                                    Next Step Scheduled
                                                </Typography>
                                                <Typography
                                                    className={
                                                        classes.fieldValue
                                                    }
                                                >
                                                    {new Date(
                                                        campaignProspectData.next_step_scheduled_at,
                                                    ).toLocaleString()}
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>

                                    {/* Engagement Metrics */}
                                    <Divider
                                        style={{ margin: theme.spacing(3, 0) }}
                                    />
                                    <Box className={classes.section}>
                                        <Typography
                                            className={classes.sectionTitle}
                                        >
                                            Engagement Metrics
                                        </Typography>
                                        <Grid container spacing={2}>
                                            <Grid item xs={4}>
                                                <Card
                                                    className={
                                                        classes.engagementCard
                                                    }
                                                >
                                                    <CardContent
                                                        className={
                                                            classes.engagementMetric
                                                        }
                                                    >
                                                        <Typography
                                                            className={
                                                                classes.engagementValue
                                                            }
                                                        >
                                                            {
                                                                campaignProspectData.messages_sent
                                                            }
                                                        </Typography>
                                                        <Typography
                                                            className={
                                                                classes.engagementLabel
                                                            }
                                                        >
                                                            Messages Sent
                                                        </Typography>
                                                    </CardContent>
                                                </Card>
                                            </Grid>
                                            <Grid item xs={4}>
                                                <Card
                                                    className={
                                                        classes.engagementCard
                                                    }
                                                >
                                                    <CardContent
                                                        className={
                                                            classes.engagementMetric
                                                        }
                                                    >
                                                        <Typography
                                                            className={
                                                                classes.engagementValue
                                                            }
                                                        >
                                                            {
                                                                campaignProspectData.messages_opened
                                                            }
                                                        </Typography>
                                                        <Typography
                                                            className={
                                                                classes.engagementLabel
                                                            }
                                                        >
                                                            Messages Opened
                                                        </Typography>
                                                    </CardContent>
                                                </Card>
                                            </Grid>
                                            <Grid item xs={4}>
                                                <Card
                                                    className={
                                                        classes.engagementCard
                                                    }
                                                >
                                                    <CardContent
                                                        className={
                                                            classes.engagementMetric
                                                        }
                                                    >
                                                        <Typography
                                                            className={
                                                                classes.engagementValue
                                                            }
                                                        >
                                                            {
                                                                campaignProspectData.messages_clicked
                                                            }
                                                        </Typography>
                                                        <Typography
                                                            className={
                                                                classes.engagementLabel
                                                            }
                                                        >
                                                            Messages Clicked
                                                        </Typography>
                                                    </CardContent>
                                                </Card>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                </>
                            ) : (
                                <Box className={classes.section}>
                                    <Typography
                                        variant="body2"
                                        style={{
                                            color: theme.palette.text.secondary,
                                        }}
                                    >
                                        No campaign information available for
                                        this lead.
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    )}
                </Box>
            </DialogContent>

            <DialogActions
                style={{
                    padding: theme.spacing(2, 3),
                    borderTop: `1px solid ${theme.palette.divider}`,
                    backgroundColor: '#f8fafc',
                    flexShrink: 0,
                }}
            >
                {onSyncLead && lead.sync_status === LeadSyncStatus.NOT_SYNCED && (
                    <Button
                        onClick={() => onSyncLead(lead.id)}
                        color="primary"
                        variant="outlined"
                        style={{
                            textTransform: 'none',
                            fontWeight: 500,
                            marginRight: 'auto',
                        }}
                    >
                        Sync to Entrata
                    </Button>
                )}
                <Button
                    onClick={onClose}
                    color="primary"
                    variant="contained"
                    style={{
                        textTransform: 'none',
                        fontWeight: 500,
                        padding: theme.spacing(0.75, 2),
                    }}
                >
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    )
}
